import { useCallback, useEffect, useRef, useState } from 'react';

import type { AccountReset, AccountSnapshot, AccountWasReset, CheckRequest, Checks, ExitChange, ExitDefaultsChange, LimitsChange, LimitsChanged, Order, OrderIntent } from './generated/types.gen';

/**
 * The account surface the order management service implements (plan 009), generated from the backend
 * models. Every amount is a decimal string computed by the backend; the browser formats and never totals money.
 */
export type {
  Account, AccountSnapshot, Checks, ExitDefaults, Fill, Limits, LimitsChange, Order, OrderIntent, Position, PositionExit, Settlement,
} from './generated/types.gen';
export type Environment = AccountSnapshot['account']['environment'];
export type Action = Order['action'];
export type TradeSide = Order['side'];
export type TimeInForce = Order['time_in_force'];
export type OrderStatus = Order['status'];
export type TradingStatus = 'connecting' | 'not_connected' | 'connected' | 'error';

export const OPEN_STATUSES: ReadonlySet<OrderStatus> = new Set(['pending', 'resting', 'partially_filled']);
export const isOpen = (o: Order) => OPEN_STATUSES.has(o.status);
export const NOT_CONNECTED = 'Trading isn’t connected yet. Orders can be reviewed but not sent until an execution venue is set up.';

const POLL_MS = 2000, ABSENT_RETRY_MS = 30000;

/** Plan 044: this build's order-safety version. The server refuses orders from older builds. */
export const CLIENT_VERSION = '44';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'X-CT-Client': CLIENT_VERSION, ...(body ? { 'Content-Type': 'application/json' } : {}) };
  const r = await fetch(path, { method, cache: 'no-store', headers, body: body ? JSON.stringify(body) : undefined });
  if (!r.ok) {
    let detail = `Request failed (${r.status})`;
    try { const j = await r.json() as { detail?: unknown }; if (typeof j.detail === 'string') detail = j.detail; } catch { /* keep status text */ }
    throw new Error(detail);
  }
  return r.status === 204 ? undefined as T : r.json() as Promise<T>;
}

export const submitOrder = (intent: OrderIntent) => request<Order>('POST', '/api/v1/orders', intent);
export const amendOrder = (id: string, change: { quantity?: string; limit_price?: string }) => request<Order>('PATCH', `/api/v1/orders/${encodeURIComponent(id)}`, change);
export const cancelOrder = (id: string) => request<Order>('DELETE', `/api/v1/orders/${encodeURIComponent(id)}`);
export const cancelAll = () => request<{ cancelled: number }>('POST', '/api/v1/orders/cancel-all');
/** Plan 010: what every guardrail says about a prospective order. Read-only; the order service decides on submit. */
export const checkOrder = (req: CheckRequest, signal?: AbortSignal) =>
  fetch('/api/v1/checks', { method: 'POST', cache: 'no-store', signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(req) })
    .then(r => r.ok ? r.json() as Promise<Checks> : Promise.reject(new Error(`Guardrail check failed (${r.status})`)));
/** Tighter values apply now; looser ones at the next day reset. */
export const setLimits = (change: LimitsChange) => request<LimitsChanged>('PUT', '/api/v1/limits', change);
/** Plan 035: start a fresh paper run. The order service refuses while contracts are held. */
export const resetAccount = (body: AccountReset) => request<AccountWasReset>('POST', '/api/v1/account/reset', body);
/** Plan 012: change or remove a held position's take-profit and stop-loss (both empty removes the exit). */
export const setExit = (ticker: string, side: TradeSide, change: ExitChange) =>
  request<unknown>('PUT', `/api/v1/positions/${encodeURIComponent(ticker)}/${side}/exit`, change);
export const setExitDefaults = (horizon: 'hourly' | '15m', change: ExitDefaultsChange) =>
  request<unknown>('PUT', `/api/v1/exit-defaults/${horizon}`, change);

/**
 * Polls the account surface while the page is visible. A missing surface (404/405/501) means no order
 * management service is configured: the UI shows "Not connected" and checks again occasionally.
 */
export function useTrading() {
  const [snapshot, setSnapshot] = useState<AccountSnapshot | null>(null);
  const [status, setStatus] = useState<TradingStatus>('connecting');
  const [detail, setDetail] = useState('');
  const wake = useRef<() => void>(() => {});
  useEffect(() => {
    let dead = false, timer: ReturnType<typeof setTimeout> | undefined, controller: AbortController | undefined;
    const schedule = (ms: number) => { clearTimeout(timer); timer = setTimeout(load, ms); };
    async function load() {
      if (dead) return;
      if (document.visibilityState === 'hidden') { schedule(POLL_MS); return; }
      controller?.abort(); controller = new AbortController();
      try {
        const r = await fetch('/api/v1/account', { cache: 'no-store', signal: controller.signal });
        if ([404, 405, 501].includes(r.status)) { setSnapshot(null); setStatus('not_connected'); setDetail(NOT_CONNECTED); schedule(ABSENT_RETRY_MS); return; }
        if (!r.ok) throw new Error(`Account unavailable (${r.status})`);
        setSnapshot(await r.json() as AccountSnapshot); setStatus('connected'); setDetail('');
        schedule(POLL_MS);
      } catch (e) {
        if (dead || (e instanceof DOMException && e.name === 'AbortError')) return;
        // Keep the last snapshot visible but mark it stale; orders stay blocked until it refreshes.
        setStatus('error'); setDetail(e instanceof Error ? e.message : 'Account unavailable'); schedule(POLL_MS * 2);
      }
    }
    wake.current = () => schedule(0);
    void load();
    const visible = () => { if (document.visibilityState === 'visible') schedule(0); };
    document.addEventListener('visibilitychange', visible);
    return () => { dead = true; clearTimeout(timer); controller?.abort(); document.removeEventListener('visibilitychange', visible); };
  }, []);
  const refresh = useCallback(() => wake.current(), []);
  return { snapshot, status, detail, refresh };
}
export type Trading = ReturnType<typeof useTrading>;
